import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button, Col, Modal, Row, Skeleton, Table, Tag } from "antd";

import {
  constructDisplayNumber,
  constructDisplayPercentage,
  constructDisplayPrice,
  getLabelOfConstant,
} from "utils/general";
import {
  useGetAdviseeProposal,
  patchAdviseeProposalPolicyStatuses,
  patchAddedProposalPolicies,
  patchExistingProposalPolicies,
  patchResetExistingProposalPolicies,
} from "apis/advisee";
import { useFetchConstant } from "apis/constant";

import Title from "components/Title/Title";
import PolicySection from "components/PolicySection/PolicySection";
import SectionContainer from "components/SectionContainer/SectionContainer";
import AddPolicyModal from "components/AddPolicyModal/AddPolicyModal";

import {
  CardContainer,
  CoverageLabel,
  CoverageText,
  TableButtons,
} from "./Proposal.styles";

import Locale from "react-intl-universal";

const { confirm } = Modal;

const TABLE_DEACTIVATED_BG_COLOR = "#E6E6E6";
const TABLE_DEACTIVATED_TEXT_COLOR = "#A5A5A5";

const TextButton = ({ text, onClick, disabled }) => {
  return (
    <Button type="text" onClick={onClick} disabled={disabled}>
      {text}
    </Button>
  );
};

const constructCoverageData = (coverage) => [
  {
    label: Locale.get("proposal.required"),
    value: constructDisplayPrice(coverage.required, false),
  },
  {
    label: Locale.get("proposal.current"),
    value: constructDisplayPrice(coverage.current, false),
  },
  {
    label: Locale.get("proposal.valuePropose"),
    value: constructDisplayPrice(coverage.proposed, false),
  },
  {
    label: Locale.get("proposal.balance"),
    value: constructDisplayPrice(coverage.balance, false),
  },
  {
    label: Locale.get("proposal.ifs"),
    value: constructDisplayPercentage(coverage.ifs),
  },
];

const constructPoliciesColumns = (
  planTypes,
  insuranceCompanies,
  policyStatuses,
  proposalPolicyStatuses,
  isProposalActive,
  onPolicyEditButtonClick,
  onPolicyResetButtonClick
) => {
  const generateRenderProps = (children, record) => ({
    props: {
      ...(record.proposedPolicyStatus ===
        proposalPolicyStatuses.REMOVED.code && {
        style: {
          background: TABLE_DEACTIVATED_BG_COLOR,
          color: TABLE_DEACTIVATED_TEXT_COLOR,
        },
      }),
    },
    children,
  });

  return [
    {
      title: Locale.get("proposal.planNo"),
      dataIndex: "policyNumber",
      key: "policyNumber",
      render: (value, record) =>
        generateRenderProps(
          <Row gutter={4}>
            <Col>{value}</Col>
          </Row>,
          record
        ),
    },
    {
      title: Locale.get("proposal.planName"),
      dataIndex: "name",
      key: "name",
      render: (value, record) => {
        return generateRenderProps(
          <Row gutter={4}>
            <Col>{value}</Col>
            <Col>
              {record.coveredFor && (
                <Tag color="orange">{record.coveredFor}</Tag>
              )}
            </Col>
          </Row>,
          record
        );
      },
    },
    {
      title: Locale.get("proposal.company"),
      dataIndex: "company",
      key: "company",
      render: (value, record) =>
        generateRenderProps(
          getLabelOfConstant(value.type, insuranceCompanies),
          record
        ),
    },
    {
      title: Locale.get("proposal.planType"),
      dataIndex: "type",
      key: "type",
      render: (value, record) =>
        generateRenderProps(getLabelOfConstant(value, planTypes), record),
    },
    {
      title: Locale.get("proposal.monthlyPremium"),
      dataIndex: "monthlyPremium",
      key: "monthlyPremium",
      render: (value, record) =>
        generateRenderProps(
          `${constructDisplayNumber(value)} (${constructDisplayNumber(
            record.yearlyPremium
          )}/yr)`,
          record
        ),
    },
    {
      title: Locale.get("proposal.status"),
      dataIndex: "status",
      key: "status",
      render: (value, record) =>
        generateRenderProps(getLabelOfConstant(value, policyStatuses), record),
    },
    {
      dataIndex: "proposedPolicyStatus",
      key: "proposedPolicyStatus",
      render: (value, record) =>
        generateRenderProps(
          value !== proposalPolicyStatuses.REMOVED.code && isProposalActive ? (
            <>
              <TextButton
                text={Locale.get("proposal.edit")}
                onClick={onPolicyEditButtonClick(record._id)}
              />
              <TextButton
                text={Locale.get("proposal.reset")}
                onClick={onPolicyResetButtonClick(record._id)}
                disabled={!record.hasChangedFields}
              />
            </>
          ) : (
            ""
          ),
          record
        ),
    },
  ];
};

const StatusTag = ({ status, proposalStatusesConst, proposalStatuses }) => {
  let tagColor =
    status === proposalStatusesConst.ARCHIVE.code ? "#4F4F4F" : "#2AC940";

  const statusLabel = getLabelOfConstant(status, proposalStatuses);
  const isStatusActive = status === proposalStatusesConst.ACTIVE.code;

  return isStatusActive ? null : (
    <Tag style={{ marginLeft: 8 }} color={tagColor}>
      {statusLabel}
    </Tag>
  );
};

const CoverageCard = ({ data = {}, order, title }) => {
  const coverageData = constructCoverageData(data);
  return (
    <>
      <Col span={8} lg={8} order={order}>
        <CardContainer>
          <CoverageLabel>{title}</CoverageLabel>
          {coverageData.map((data) => (
            <Row key={data.label}>
              <Col span={24} xl={12}>
                <CoverageText style={{ width: 500 }}>
                  <b>{data.label} :</b>
                </CoverageText>
              </Col>
              <Col span={24} xl={12}>
                <CoverageText>{data.value}</CoverageText>
              </Col>
            </Row>
          ))}
        </CardContainer>
      </Col>
    </>
  );
};

const PremiumCard = ({ label, value, order }) => {
  return (
    <>
      <Col span={8} order={order}>
        <CardContainer>
          <Row key={label}>
            <CoverageText style={{ marginRight: 16 }}>
              <b>{label} : </b>
            </CoverageText>
            <CoverageText>{constructDisplayPrice(value, false)}</CoverageText>
          </Row>
        </CardContainer>
      </Col>
    </>
  );
};

const Proposal = () => {
  const { id: adviseeId, proposalId } = useParams();
  const [selectedExistingPolicies, setSelectedExistingPolicies] = useState([]);
  const [selectedPolicyToEdit, setSelectedPolicyToEdit] = useState();

  const {
    isLoading: isPlanTypesLoading,
    selection: planTypes,
  } = useFetchConstant("planTypes");
  const {
    isLoading: isInsuranceCompaniesLoading,
    selection: insuranceCompanies,
  } = useFetchConstant("insuranceCompanies");
  const {
    isLoading: isProposalStatusesLoading,
    data: proposalStatusesConst,
    selection: proposalStatuses,
  } = useFetchConstant("proposalStatuses");
  const {
    isLoading: isProposalPolicyStatusesLoading,
    data: proposalPolicyStatuses,
  } = useFetchConstant("proposalPolicyStatuses");
  const {
    isLoading: isPolicyStatusesLoading,
    selection: policyStatuses,
  } = useFetchConstant("policyStatuses");
  const {
    isLoading: isProposalDetailsLoading,
    data: proposalDetails,
  } = useGetAdviseeProposal(adviseeId, proposalId);

  const isLoading =
    isPlanTypesLoading ||
    isInsuranceCompaniesLoading ||
    isProposalDetailsLoading ||
    isProposalStatusesLoading ||
    isProposalPolicyStatusesLoading ||
    isPolicyStatusesLoading;
  const isProposalActive = useMemo(
    () =>
      !isProposalStatusesLoading &&
      proposalDetails.status === proposalStatusesConst.ACTIVE.code,
    [proposalDetails, proposalStatusesConst, isProposalStatusesLoading]
  );

  const handleOnUpdatePolicies = (policies) => {
    patchAddedProposalPolicies(adviseeId, proposalId, { policies });
  };

  const handleOnDeactivateButtonClick = () => {
    confirm({
      content: Locale.get("proposal.contentRemove"),
      onOk() {
        patchAdviseeProposalPolicyStatuses(adviseeId, proposalId, {
          status: proposalPolicyStatuses.REMOVED.code,
          policyIds: selectedExistingPolicies,
        });
      },
    });
  };

  const handleOnActivateButtonClick = () => {
    confirm({
      title: Locale.get("proposal.contentActivate"),
      onOk() {
        patchAdviseeProposalPolicyStatuses(adviseeId, proposalId, {
          status: proposalPolicyStatuses.EXISTING.code,
          policyIds: selectedExistingPolicies,
        });
      },
    });
  };

  const handleOnPolicyEditButtonClick = (selectedPolicyId) => () => {
    const selectedPolicy = proposalDetails.existingProposedPolicies.find(
      (policy) => policy._id === selectedPolicyId
    );
    setSelectedPolicyToEdit(selectedPolicy);
  };

  const handleOnPolicyResetButtonClick = (selectedPolicyId) => () => {
    confirm({
      title:
        "Are you sure you want to reset this policy to its original state?",
      content: "You will lose all changes made on this policy.",
      onOk() {
        patchResetExistingProposalPolicies(
          adviseeId,
          proposalId,
          selectedPolicyId
        );
      },
    });
  };

  const handleOnPolicyModalOk = (payload) => {
    patchExistingProposalPolicies(
      adviseeId,
      proposalId,
      selectedPolicyToEdit._id,
      payload
    );
    setSelectedPolicyToEdit();
  };

  return (
    <>
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          <Row align="middle">
            <Title marginBottom="8px">
              {" "}
              {Locale.get("proposal.proposalDetailName")} {proposalDetails.name}
            </Title>
            <StatusTag
              status={proposalDetails.status}
              proposalStatusesConst={proposalStatusesConst}
              proposalStatuses={proposalStatuses}
            />
          </Row>
          {proposalDetails.statistics && (
            <SectionContainer>
              <Row gutter={[24, 16]}>
                <CoverageCard
                  order={1}
                  data={proposalDetails.statistics.deathCoverage}
                  title={Locale.get("proposal.deathCoverage")}
                />
                <CoverageCard
                  order={2}
                  data={proposalDetails.statistics.tpdCoverage}
                  title={Locale.get("proposal.tpdCoverage")}
                />
                <CoverageCard
                  order={3}
                  data={proposalDetails.statistics.ciCoverage}
                  title={Locale.get("proposal.ciCoverage")}
                />

                <PremiumCard
                  order={4}
                  label={Locale.get("proposal.premiumRemove")}
                  value={proposalDetails.statistics.premiumRemoved}
                />
                <PremiumCard
                  order={5}
                  label={Locale.get("proposal.premiumAdd")}
                  value={proposalDetails.statistics.premiumAdded}
                />
                <PremiumCard
                  order={6}
                  label={Locale.get("proposal.totalPremium")}
                  value={proposalDetails.statistics.totalPremium}
                />
              </Row>
            </SectionContainer>
          )}
          <SectionContainer>
            <Title marginBottom="8px">
              {Locale.get("proposal.existingPolicy")}
            </Title>
            <div>
              {isProposalActive && (
                <>
                  <TableButtons
                    type="ghost"
                    onClick={handleOnActivateButtonClick}
                    disabled={selectedExistingPolicies.length === 0}
                  >
                    {Locale.get("proposal.includeProposal")}
                  </TableButtons>
                  <TableButtons
                    type="ghost"
                    danger
                    onClick={handleOnDeactivateButtonClick}
                    disabled={selectedExistingPolicies.length === 0}
                  >
                    {Locale.get("proposal.removeProposal")}
                  </TableButtons>
                </>
              )}
              <Table
                dataSource={proposalDetails.existingProposedPolicies}
                columns={constructPoliciesColumns(
                  planTypes,
                  insuranceCompanies,
                  policyStatuses,
                  proposalPolicyStatuses,
                  isProposalActive,
                  handleOnPolicyEditButtonClick,
                  handleOnPolicyResetButtonClick
                )}
                pagination={false}
                rowKey={(record) => record._id}
                style={{ width: "100%" }}
                rowSelection={
                  isProposalActive && {
                    onChange: setSelectedExistingPolicies,
                    renderCell: (checked, record, index, originNode) => ({
                      props: {
                        ...(record.proposedPolicyStatus ===
                          proposalPolicyStatuses.REMOVED.code && {
                          style: {
                            background: TABLE_DEACTIVATED_BG_COLOR,
                            color: TABLE_DEACTIVATED_TEXT_COLOR,
                          },
                        }),
                      },
                      children: originNode,
                    }),
                  }
                }
                locale={{ emptyText: "No Existing Policies" }}
                scroll={{ x: 432 }}
              />
            </div>
          </SectionContainer>
          {((proposalDetails.addedProposedPolicies &&
            proposalDetails.addedProposedPolicies.length) ||
            isProposalActive) && (
            <SectionContainer>
              <Title marginBottom="8px">
                {Locale.get("proposal.proposedNewPolicy")}
              </Title>
              <PolicySection
                policies={proposalDetails.addedProposedPolicies}
                updatePolicies={handleOnUpdatePolicies}
                isForProposal
                hideAddButton={!isProposalActive}
                disableEditButton={!isProposalActive}
                disableRemoveButton={!isProposalActive}
              />
            </SectionContainer>
          )}
          <AddPolicyModal
            defaultValues={selectedPolicyToEdit}
            isForProposal
            visible={!!selectedPolicyToEdit}
            onCancel={() => setSelectedPolicyToEdit()}
            onOk={handleOnPolicyModalOk}
          />
        </>
      )}
    </>
  );
};

export default Proposal;
